import cn from "classnames";
import { graphql } from "gatsby";
import parse from "html-react-parser";

import { useSetIsNavigationStuck } from "@core/application/navigation-use-cases";
import { CountryCode } from "@settings/countries";
import useVisibilityTracker from "@utils/hooks/useVisibilityTracker";

import PetSpeciesSelector from "@organisms/pet-species-selector/PetSpeciesSelector";
import TrustPilotReviews from "@organisms/trustpilot-reviews/TrustPilotReviews";

import * as styles from "@organisms/hero/Hero.module.scss";

export interface HeroProps {
  data: {
    title: string;
    subtitle?: string;
    list?: string[];
    reviewsLink?: string;
    shouldShowPetSpeciesSelector?: boolean;
    image?: {
      file: {
        url: string;
      };
    };
  };
  countryCodeIso?: CountryCode;
  adoptionClassName?: string;
}

const Hero = ({
  data: { title, subtitle, list, reviewsLink, shouldShowPetSpeciesSelector, image },
  countryCodeIso = CountryCode.ES,
  adoptionClassName,
}: HeroProps): JSX.Element => {
  const { ref, isVisible } = useVisibilityTracker("hero", {
    amount: 0.5,
    margin: "250px 0px 0px 0px",
  });

  useSetIsNavigationStuck(!isVisible);

  const renderList = (items: string[] | undefined) => {
    if (!items) {
      return null;
    }

    return (
      <ul className={styles.heroMainFeatures} aria-label="health-basic-main-features">
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    );
  };

  return (
    <header ref={ref} className={cn(styles.hero, adoptionClassName)}>
      <h1 className={styles.title}>{parse(title)}</h1>
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      {renderList(list)}
      {reviewsLink && (
        <TrustPilotReviews adoptionClassName={styles.trustPilotReviews} reviewsLink={reviewsLink} />
      )}
      {shouldShowPetSpeciesSelector && (
        <PetSpeciesSelector
          adoptionClassName={styles.petSpeciesSelector}
          countryCodeIso={countryCodeIso}
        />
      )}
      {image && <img className={styles.image} src={image.file.url} alt={title} />}
    </header>
  );
};

export const query = graphql`
  fragment HeroFragment on ContentfulHero {
    title
    subtitle
    list
    reviewsLink
    shouldShowPetSpeciesSelector
    image {
      file {
        url
      }
    }
  }
`;

export default Hero;
